import axios from 'axios';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import i18next from 'i18next'; // Use singleton — NOT the local config file
import { store } from 'store';
import { logoff, refreshTokenSucess } from 'store/auth';
import { Dialog } from 'Components/ui';
import { HttpStatus } from 'utils/http_status_utils';

/**
 * Helper function to retrieve the CURRENT auth and refresh tokens.
 *
 * Reads the live Redux store, not localStorage. redux-persist's write of a fresh token (e.g.
 * from switchTenantSuccess) to localStorage is async/debounced relative to the store update
 * itself, which is synchronous. A request built in that window used to read the token from
 * localStorage and could grab the PREVIOUS tenant's still-persisted token while the query key
 * driving that same request had already moved on to the NEW tenant (selectedTenant updates in
 * the same synchronous dispatch as the token) — the backend then correctly resolved the
 * previous tenant from that stale token, and its response got cached under the new tenant's
 * query key, cross-contaminating it (e.g. Settings > Branding briefly showing the prior
 * tenant's logo/colors after switching tenants back and forth). store.getState() has no such
 * gap: it reflects the same dispatch, in the same tick, as the query key read off useSelector.
 * Safe even on a hard page load — PersistGate (src/index.js) blocks the first render (and so
 * the first API call) until redux-persist's rehydration has already populated the store.
 */
export const getAuthAndRefreshToken = () => {
  const auth = store.getState()?.auth;
  return {
    token: auth?.token || null,
    refreshToken: auth?.refreshToken || null,
  };
};

/**
 * Get API base URL from environment variable
 * Defaults to current hostname without port for multi-tenant support
 *
 * For local development:
 * - Frontend at http://vo.localhost:3000 → API at http://vo.localhost (port 80, Nginx)
 * - Frontend at http://company.localhost:3000 → API at http://company.localhost (port 80, Nginx)
 */
const getBaseURL = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Dynamically construct base URL from current hostname (tenant-aware)
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol; // http: or https:
    const hostname = window.location.hostname; // vo.localhost, company.localhost, etc.
    return `${protocol}//${hostname}`;
  }

  // Fallback for server-side rendering
  return 'http://localhost';
};

/**
 * apiClientInternal - Internal axios instance with no interceptors
 *
 * Used for:
 * - Token refresh calls (avoids circular dependency)
 *
 * No interceptors to prevent infinite loops during token refresh
 */
export const apiClientInternal = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * apiClient - Authenticated axios instance
 *
 * Replaces: backendService, backendPrivate, backendPolicy, backendMapping
 *
 * Features:
 * - Auto JWT token injection
 * - Auto token refresh when expired
 * - 401 unauthorized handling with session expiry modal
 *
 * Use for: All authenticated API calls to microservices
 */
export const apiClient = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * Request interceptor for apiClient
 * - Injects JWT token from localStorage
 * - Refreshes token if expired before request
 */
apiClient.interceptors.request.use(
  async config => {
    const { token, refreshToken } = getAuthAndRefreshToken();
    if (!config.headers?.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!token) return config;

    const user = jwtDecode(token);
    const isExpired = dayjs.unix(user.exp).diff(dayjs()) < 1;
    if (!isExpired) return config;

    // Token is expired, refresh it
    const response = await apiClientInternal.post(`/user/auth/refresh-token`, {
      refreshToken,
    });
    if (response.status === 200 && !!response.data) {
      config.headers.Authorization = `Bearer ${response.data.token}`;
      store.dispatch(refreshTokenSucess(response.data));
    }
    return config;
  },
  error => Promise.reject(error),
);

/**
 * Response interceptor for apiClient
 * - Handles 401 unauthorized errors
 * - Shows session expiry modal
 * - Redirects to login page
 */
apiClient.interceptors.response.use(
  response => response,
  async error => {
    if (error.response && error.response.status === HttpStatus.Unauthorized) {
      const result = await Dialog.fire({
        title: i18next.t('session.expiredTitle'),
        text: i18next.t('session.expiredMessage'),
        icon: 'warning',
        backdrop: true,
        showCancelButton: false,
        confirmButtonColor: 'var(--vo-danger)',
        confirmButtonText: 'OK',
      });

      if (result.isConfirmed) {
        store.dispatch(logoff());
        window.location.replace('/login');
      }

      return Promise.reject('Unauthorized');
    } else {
      return Promise.reject(error);
    }
  },
);

/**
 * Accept-Language interceptor for apiClient
 * Added AFTER the JWT token interceptor — order: token injection → language header → request
 */
apiClient.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = i18next.language || 'en';
  return config;
});

/**
 * apiClientPublic - Public axios instance with no authentication
 *
 * Replaces: backendServicePublic
 *
 * Features:
 * - No authentication headers
 * - No interceptors
 *
 * Use for: Public endpoints (login, registration, public policies, etc.)
 */
export const apiClientPublic = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * Accept-Language interceptor for apiClientPublic
 * Also adds language header for unauthenticated endpoints (e.g., country config)
 */
apiClientPublic.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = i18next.language || 'en';
  return config;
});
